/**
 * Four roles. The first is the platform; the rest are one contractor's job.
 *
 *   admin      — the master admin: BlueTape Pro itself. ONE sign-in, named by
 *                its email (MASTER_EMAIL) and by nothing on any record. It
 *                belongs to no account and is listed in none; it opens any
 *                account from the master dashboard and works in it as that
 *                account would. `role: 'admin'` on a record is a spelling the
 *                master's own record may carry; it grants nothing by itself.
 *   contractor — the GC. Admin of their OWN account and nothing else: their
 *                projects, their vendors, their techs. Not a lesser admin —
 *                a different scope.
 *   vendor     — the sub company. An account of its OWN (since 2026-09-13): it owns
 *                its crew and its list of jobs. An item reaches it by EMAIL — the
 *                contractor assigns work to the vendor's email address, and whoever
 *                signs in with that address sees it. The invite link is only a
 *                direct way to set the account up. Assigns its own techs.
 *   tech       — the person doing the work. Only what's assigned to them. Lives in
 *                whichever account invited them — a contractor's in-house crew or a
 *                vendor's.
 *
 * 'super' was an earlier spelling of contractor and is still read as one.
 */
export type Role = 'admin' | 'contractor' | 'vendor' | 'tech' | 'super';
export type RealRole = 'admin' | 'contractor' | 'vendor' | 'tech' | 'none';

/**
 * The one master admin of the whole app — Stephen's own sign-in. Keyed on the
 * email from the auth token and nothing else, so no account, no invite and no
 * record can grant it, and no record needs to exist for it to hold.
 * `firestore.rules` names the same address in `isMaster()`; the two must match.
 */
export const MASTER_EMAIL = 'stephenstocklin@gmail.com';
export const isMasterEmail = (email: string | null | undefined) => normEmail(email) === MASTER_EMAIL;
/** The master admin. Everything a contractor may do, on every account. */
export const isMasterAdmin = (u: { email?: string } | null | undefined) => isMasterEmail(u?.email);

export function roleOf(
  u: { role?: Role; email?: string; suspended?: boolean; deletedAt?: string } | null | undefined,
): RealRole {
  // Suspended is the same as malformed: no role, and a login with no role may do
  // nothing. `myRole()` in the rules says it in the same order — master first, so
  // the master cannot be locked out of their own app.
  if (u?.suspended && !isMasterAdmin(u)) return 'none';
  // A login inside an account that has been deleted is inert for the whole
  // 30-day window. The only screen it reaches is Restore, which clears the
  // stamp; after the purge there is no record and no sign-in left at all.
  if (u?.deletedAt && !isMasterAdmin(u)) return 'none';
  const r = u?.role;
  if (r === 'vendor' || r === 'tech') return r;
  // 'admin' is real only on the master's own sign-in — the rules say the same.
  if (r === 'admin') return isMasterAdmin(u) ? 'admin' : 'none';
  if (r === 'contractor' || r === 'super') return 'contractor';
  return 'none';                           // malformed profiles fail closed
}
/** The deletion window, in days. */
/** The recovery window. Stephen dropped it from 30 to 7 on 2026-09-17.
 *  Every screen and both server entry points read this; a stamp already
 *  written keeps the `purgeAt` it was given, so lowering this never brings
 *  an existing deletion forward. */
export const PURGE_DAYS = 7;
/** When a deletion stamped now becomes purgeable. */
export const purgeAtFrom = (at: string) =>
  new Date(new Date(at).getTime() + PURGE_DAYS * 86400_000).toISOString();
/** Whole days left before the purge may take it; 0 once it is due. */
export function daysLeft(purgeAt?: string): number {
  if (!purgeAt) return PURGE_DAYS;
  const ms = new Date(purgeAt).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 86400_000);
}
/** Deleted, and still inside its window. */
export const isPendingDelete = (u: { deletedAt?: string } | null | undefined) => !!u?.deletedAt;

export const isContractor  = (u: { role?: Role; email?: string } | null | undefined) => roleOf(u) === 'contractor';
export const isVendorUser  = (u: { role?: Role; email?: string } | null | undefined) => roleOf(u) === 'vendor';
export const isTechUser    = (u: { role?: Role; email?: string } | null | undefined) => roleOf(u) === 'tech';

/** Runs a job: a contractor on their own account, or the master on the dashboard.
 *  Keyed on the ROLE, not the master email: the master editing a vendor's
 *  account acts as that vendor (see `actAs`) and must see the vendor's app. */
export const runsJob = (u: { role?: Role; email?: string } | null | undefined) =>
  roleOf(u) === 'admin' || roleOf(u) === 'contractor';

/** Only the contractor (or the founding admin) decides which vendor carries an item. */
export const canAssignVendor = runsJob;
/** A vendor assigns its own techs; whoever runs the job can assign anyone's. */
export const canAssignTech = (u: { role?: Role; email?: string } | null | undefined) =>
  runsJob(u) || isVendorUser(u);
/** Rosters, project delete, imports, the overrun review. */
export const canManageProject = runsJob;
/** The Crew screen: a contractor runs their account's people and their vendor links;
 *  a vendor runs its own techs. */
export const canManageCrew = (u: { role?: Role; email?: string } | null | undefined) =>
  runsJob(u) || isVendorUser(u);

/**
 * Which contractor account a user or project belongs to. An empty string is the
 * founding account — that's what every document written before accounts existed
 * is treated as.
 */
/** A signup with no invite belongs to nobody. Keeping it out of the founding
 *  account stops strangers pooling into the first contractor's crew list. */
export const UNASSIGNED = '__unassigned';

export const accountOf = (x: { accountId?: string } | null | undefined) => x?.accountId ?? '';
export const sameAccount = (a: { accountId?: string } | null | undefined,
                            b: { accountId?: string } | null | undefined) =>
  accountOf(a) === accountOf(b);

/**
 * A contractor or vendor account is not usable until it says who it is: the
 * company, the person to call, and their number. The email is the sign-in and
 * needs no asking. Techs are not gated — they are added by their crew, and the
 * contractor already knows how to reach them.
 */
export const needsProfile = (u: AppUser | null | undefined) =>
  !!u && !isMasterAdmin(u) && (runsJob(u) || isVendorUser(u))
  && !((u.company ?? '').trim() && (u.name ?? '').trim() && (u.phone ?? '').trim());

/** The owner of an account: the login marked `owner`, or — before any transfer —
 *  the founder, whose uid is the account id. The founding master account owns the
 *  founding account. */
export const isOwner = (u: { uid: string; role?: Role; email?: string; accountId?: string; owner?: boolean } | null | undefined) =>
  !!u && (isMasterAdmin(u) || (u.owner ?? accountOf(u) === u.uid));

/** Whether this person may rename the company on their own record: the owner
 *  named it, and a contractor runs their account. Anyone who joined by code had
 *  it fixed by the invite — the rules refuse the change, so the field is locked
 *  rather than refusable. */
export const canRenameCompany = (u: AppUser) => runsJob(u) || isOwner(u);

export const ROLE_LABEL: Record<RealRole, string> = {
  admin: 'Master Admin', contractor: 'Builder', vendor: 'Vendor', tech: 'Field Tech', none: 'Not Set Up',
};
export type Status = 'open' | 'progress' | 'done';

export interface AppUser {
  uid: string;
  email: string;
  /** The main contact: the person a contractor or vendor calls about this
   *  account. Set on the profile screen straight after sign-up. */
  name: string;
  /** How to reach that person. Required of every contractor and vendor account. */
  phone?: string;
  /** When the profile was last completed on the profile screen. */
  profileSetAt?: string;
  /** Switched off by the master. Every login on the account carries its own flag —
   *  there is no account document to hang it on — so suspending one is a write per
   *  login, and `roleOf` reads it as no role at all. */
  suspended?: boolean;
  suspendedAt?: string;
  /** Account deletion, Stephen 2026-09-17: "The account will be saved for 30
   *  days and then will be fully deleted." Deleting stamps every login on the
   *  account — there is no account document, the same reason `suspended` is
   *  per-login — and each one is inert until it is restored or purged. */
  deletedAt?: string;
  /** When the purge may take it: `deletedAt` + PURGE_DAYS, ISO. The scheduled
   *  function reads this and nothing else. */
  purgeAt?: string;
  /** Who pressed delete, for the record the master sees. */
  deletedBy?: string;
  role: Role;
  /** Display name of the company. For a vendor it is their own name for
   *  themselves; the join to a contractor's items is a Link, not this string. */
  company: string;
  /** Project ids this user may see. Admins ignore this and see everything. */
  projects: string[];
  /** The account this user belongs to. '' is the founding account; a vendor's is
   *  their own uid; a tech's is whichever account invited them. */
  accountId?: string;
  /** Vendor only, legacy: seat ids from the short-lived link model. Unused. */
  links?: string[];
  /** The code this account was created with, or last redeemed. */
  inviteCode?: string;
  /** Vendors: the shop email builders reach the company at. Contact detail, not a key. */
  contactEmail?: string;
  /** The one owner of the account. Absent means "the founder is the owner" —
   *  the login whose uid is the account id — until a transfer writes it. */
  owner?: boolean;
  /** Vendor admins only: the company's key — the address the builder hands work
   *  to, which is the founder's sign-in email. A second login on the vendor's
   *  account carries it so it sees the company's work. Absent on the founder. */
  vendorKey?: string;
  createdAt: string;
}

/** The address a vendor's work is keyed to: the founder's own email, or the
 *  key a vendor admin was given. Builders and techs simply get their email. */
export const vendorKeyOf = (u: { email?: string; vendorKey?: string } | null | undefined) =>
  normEmail(u?.vendorKey || u?.email);

/* ---------------- the master dashboard ---------------- */

/**
 * One account as the master dashboard lists it — derived from the user records
 * and the projects, since no account document exists. '' is the founding
 * account. The master's own record is never a member of anything.
 */
export interface AccountSummary {
  id: string;
  kind: 'builder' | 'vendor';
  company: string;
  /** The owner: flagged, else the founder (uid == account id), else the first admin. */
  owner?: AppUser;
  /** Every login on the account — builder or vendor admins, legacy tech logins. */
  members: AppUser[];
  jobs: Project[];
  /** Vendors: the address the work is keyed to (the founder's sign-in, or the
   *  key its admins carry). Builders: ''. */
  key: string;
}

/**
 * The role written on the record, ignoring whether the account is switched off.
 * `roleOf` deliberately reads a suspended login as having no role at all — that
 * is what makes suspension work — but the master's dashboard still has to say
 * what an account IS while it is off, or a suspended vendor would be listed as a
 * builder and there would be no way to tell what you were switching back on.
 */
export const filedRole = (u: AppUser): RealRole => roleOf({ ...u, suspended: false });

/** Every login on the account is switched off. Empty accounts are not suspended. */
/** Deleted by its own owner and inside the 30-day window. Empty accounts are
 *  not deleted; one stamped login among several is a person leaving, not the
 *  account closing. */
export const accountDeleted = (a: { members: AppUser[] }) =>
  a.members.length > 0 && a.members.every(m => !!m.deletedAt);
/** Days left before the purge may take the account. */
export const accountDaysLeft = (a: { members: AppUser[] }) =>
  daysLeft(a.members.map(m => m.purgeAt).filter(Boolean).sort()[0]);

export const accountSuspended = (a: { members: AppUser[] }) =>
  a.members.length > 0 && a.members.every(m => m.suspended === true);

export function accountsOf(users: AppUser[], projects: Project[]): AccountSummary[] {
  const ids = new Set<string>();
  const people = users.filter(u => !isMasterAdmin(u) && accountOf(u) !== UNASSIGNED);
  people.forEach(u => ids.add(accountOf(u)));
  projects.forEach(p => ids.add(accountOf(p)));
  const out: AccountSummary[] = [];
  for (const id of ids) {
    const members = people.filter(u => accountOf(u) === id)
      .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
    const jobs = projects.filter(p => accountOf(p) === id).sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
    const contractors = members.filter(u => filedRole(u) === 'contractor');
    const vendors = members.filter(u => filedRole(u) === 'vendor');
    const founder = members.find(u => u.uid === id);
    const owner = members.find(u => u.owner) ?? founder ?? contractors[0] ?? vendors[0];
    // A builder's account may still carry an old-style vendor record (from
    // before vendors had accounts); one contractor login makes it a builder.
    const kind: AccountSummary['kind'] =
      !contractors.length && owner && filedRole(owner) === 'vendor' ? 'vendor' : 'builder';
    const company = (owner?.company || contractors.find(u => u.company)?.company || jobs.find(p => p.ownerCompany)?.ownerCompany
      || (id === '' ? 'Founding account' : `Account ${id.slice(0, 8)}`)).trim();
    const key = kind === 'vendor'
      ? normEmail(founder?.email || members.find(u => u.vendorKey)?.vendorKey || owner?.email)
      : '';
    out.push({ id, kind, company, owner, members, jobs, key });
  }
  return out.sort((a, b) => a.company.localeCompare(b.company));
}

/**
 * The master, working inside one account: the same sign-in, wearing that
 * account's role, company and key, so every read is scoped and every write is
 * stamped exactly as the account's own admin would have it. The email stays the
 * master's — that is what the rules let through — and the audit trail names them.
 */
export function actAs(master: AppUser, a: AccountSummary): AppUser {
  const { vendorKey: _k, owner: _o, ...rest } = master;
  return {
    ...rest,
    role: a.kind === 'vendor' ? 'vendor' : 'contractor',
    accountId: a.id,
    company: a.company,
    projects: [],
    ...(a.kind === 'vendor' ? { vendorKey: a.key } : {}),
  };
}

/** Which account the master is working in, remembered across a reload. */
const MASTER_VIEW_KEY = 'bluetape_master_view';
export function readMasterView(): AccountSummary | null {
  try {
    const raw = localStorage.getItem(MASTER_VIEW_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v.id === 'string' && (v.kind === 'builder' || v.kind === 'vendor')
      ? { id: v.id, kind: v.kind, company: String(v.company ?? ''), members: [], jobs: [], key: String(v.key ?? '') }
      : null;
  } catch { return null; }
}
export function writeMasterView(v: AccountSummary | null) {
  try {
    if (!v) localStorage.removeItem(MASTER_VIEW_KEY);
    else localStorage.setItem(MASTER_VIEW_KEY, JSON.stringify({ id: v.id, kind: v.kind, company: v.company, key: v.key }));
  } catch { /* private mode */ }
}

/**
 * A field tech. Not an account (since 2026-09-13 late, Stephen's call: "the techs
 * should not have to create an account"): a person the crew owner reaches by
 * phone. Adding one mints an opaque id — the link — and a four-digit PIN; the
 * link opens a page that shows only the items handed to them. The tech doc lives
 * in the account that added them: a contractor's in-house crew, or a vendor's own.
 */
export interface Tech {
  /** Opaque, and the whole of the link: /t/{id}. */
  id: string;
  /** The account whose crew this is — '' founding, a contractor's, or a vendor's uid. */
  accountId: string;
  /** The sign-in email of whoever added them, lowercased. A vendor's tech reaches a
   *  contractor's job through the vendor's grant, which is keyed by this address. */
  ownerEmail: string;
  name: string;
  phone: string;
  /** How they sign in without a link: this address plus the PIN. Lowercased. */
  email: string;
  /** Four digits. Sent with the link; checked by the rules when the page opens. */
  pin: string;
  createdBy: string;
  createdAt: string;
}

/** Draw an unbiased code from the platform's cryptographic random source. */
function secureCode(length: number, alphabet: string): string {
  if (typeof crypto === 'undefined' || !('getRandomValues' in crypto)) {
    throw new Error('A secure random source is required');
  }
  const ceiling = 256 - (256 % alphabet.length);
  let out = '';
  while (out.length < length) {
    const bytes = crypto.getRandomValues(new Uint8Array(Math.max(8, length - out.length)));
    for (const byte of bytes) {
      if (byte < ceiling) out += alphabet[byte % alphabet.length];
      if (out.length === length) break;
    }
  }
  return out;
}

/** A four-digit PIN — Stephen's call, twice ("I am ok with the 4 digit pin code
 *  for now"; "I want the four digit pin"): the beta lock, texted with the link and
 *  read off a phone. Drawn from the platform's cryptographic random source, with
 *  leading zeros kept. The rules check it on every read, so New PIN cuts every
 *  phone off at once, and the session it opened is deleted in the same batch. */
export const newPin = () => secureCode(4, '0123456789');
export const validPin = (pin: string) => /^\d{4}$/.test(pin);

export const techLink = (id: string, origin = typeof location !== 'undefined' ? location.origin : '') =>
  `${origin}/t/${id}`;

/** What the crew owner texts a tech. The link and the PIN are all they need. */
export function techMessage(opts: { name: string; from: string; fromCompany?: string; id: string; pin: string; origin?: string }) {
  const sender = opts.fromCompany ? `${opts.from} at ${opts.fromCompany}` : opts.from;
  const first = opts.name.trim().split(/\s+/)[0] || 'there';
  return `Hi ${first} — ${sender} put your punch list on BlueTape Pro.\n\n`
    + `Tap to open it:\n${techLink(opts.id, opts.origin)}\n\n`
    + `Your PIN is ${opts.pin}. It shows only what's on you — tap Start when you begin an item and Done when it's finished.`;
}

/**
 * The client — the homeowner, the person the job is for. Not an account, like a
 * tech: the contractor adds them against ONE job and texts a link and a PIN. The
 * link shows that job's whole list, lets them leave notes on items, and lets them
 * add items, which reach the contractor flagged "from the client" until accepted.
 */
export interface Client {
  /** Opaque, and the whole of the link: /c/{id}. */
  id: string;
  projectId: string;
  /** The contractor account that owns the job. */
  accountId: string;
  name: string;
  phone: string;
  /** How they sign in without a link: this address plus the PIN. Lowercased. */
  email: string;
  pin: string;
  createdBy: string;
  createdAt: string;
}

export const clientLink = (id: string, origin = typeof location !== 'undefined' ? location.origin : '') =>
  `${origin}/c/${id}`;

export function clientMessage(opts: { name: string; from: string; fromCompany?: string; projectName: string; id: string; pin: string; origin?: string }) {
  const sender = opts.fromCompany ? `${opts.from} at ${opts.fromCompany}` : opts.from;
  const first = opts.name.trim().split(/\s+/)[0] || 'there';
  return `Hi ${first} — ${sender} has put the punch list for ${opts.projectName} online so you can follow it.\n\n`
    + `Tap to open it:\n${clientLink(opts.id, opts.origin)}\n\n`
    + `Your PIN is ${opts.pin}. You can see what's done and what's left, leave a note on any item, and add anything we missed.`;
}

/**
 * One entry in the email directory: which link an email address opens. Written
 * when a tech or client is added, so the sign-in page can find them from the
 * email they were added with; the PIN tells two people on one address apart.
 * The key is the address lowercased (normEmail).
 */
export interface LinkEntry {
  kind: 'tech' | 'client';
  id: string;
  /** What to call it if it ever has to be shown — the crew's company, the job. */
  label: string;
  /** The account that owns the record — the only account that may write or
   *  remove this entry. One document per entry (`emailLinks/{address}/entries/
   *  {kind_id}`), so two builders sharing an address never touch each other's. */
  accountId?: string;
}

/** Whether a URL path is a client link, and which client. */
export function readClientLink(path = typeof location !== 'undefined' ? location.pathname : ''): string {
  const m = /^\/c\/([a-z0-9-]+)\/?$/i.exec(path);
  return m ? m[1] : '';
}

/** Whether a URL path is a tech link, and which tech. */
export function readTechLink(path = typeof location !== 'undefined' ? location.pathname : ''): string {
  const m = /^\/t\/([a-z0-9-]+)\/?$/i.exec(path);
  return m ? m[1] : '';
}

export interface Project {
  id: string;
  seq: number;
  name: string;
  /** The contractor's company name, so a vendor's dashboard can say whose job
   *  this is without reading a user document. Absent on older projects. */
  ownerCompany?: string;
  short: string;
  address: string;
  listName: string;
  listDate: string;
  source: string;
  areaOrder: string[];
  /** The project's rooms. Authoritative: an item is assigned to a room from
   *  this list, never the other way round. Seeded from the imported file. */
  areas?: RosterEntry[];
  /** The project's trades/vendors, same rule. */
  trades?: RosterEntry[];
  itemPrefix: string;
  /** Last human-readable item number allocated transactionally. Older projects
   *  seed this from the largest item sequence the first time somebody adds one. */
  nextItemSeq?: number;
  /** Stamped when the owning account is deleted, cleared when it is restored.
   *  Every seat's app hides a stamped job; the purge deletes it. */
  deletedAt?: string;
  /** The contractor account that owns this job. '' is the founding account. */
  accountId?: string;
  createdAt: string;
  createdBy: string;
}

/** A room or a trade. `note` is the description shown wherever it's picked. */
export interface RosterEntry {
  name: string;
  note?: string;
}

/**
 * A code a contractor hands out. Redeeming it writes role, company, projects and
 * account onto the new user at signup, so nobody types their own standing and
 * nobody lands in the wrong account.
 */
/**
 * A builder asking for access, before they have any login at all.
 *
 * Name and email, and nothing else — they name their company when they create
 * the account, which the sign-up form already asks for. Approving mints a
 * `newAccount` invite code locked to this address and emails them the join
 * link, so the path they walk is the invite path that already works rather than
 * a second way in.
 *
 * The document id is the normalized email, which makes one address one request
 * for free and keeps a refresh from filing a second.
 */
export interface SignupRequest {
  /** == normEmail(email), and the document id. */
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'approved' | 'declined';
  createdAt: string;
  decidedAt?: string;
  decidedBy?: string;
  /** The code minted on approval — what the emailed link carries. */
  code?: string;
  /** Set once the invite email has actually gone out, so a failed send is
   *  visible as an approved request nobody has heard from. */
  sentAt?: string;
}

export interface Invite {
  /** The code itself, and the document id. Short and sayable down a phone. */
  code: string;
  accountId: string;
  role: 'contractor' | 'vendor' | 'tech';
  company: string;
  projects: string[];
  /**
   * A code that starts a NEW contractor account instead of joining this one —
   * the whole of signup, for as long as signup stays invite-only. Only the
   * founding admin mints these, and the account they create is keyed by the new
   * contractor's own uid: unique by construction, and provably nobody else's.
   * A contractor minting `contractor` is the other case entirely — a second
   * admin on the account they already run — and is never `newAccount`.
   */
  newAccount?: boolean;
  /**
   * Vendor codes only: the email the work is assigned to. The account that
   * redeems the code has to sign in with this address — that is the whole join —
   * so the sign-up form locks it. Items carry it as `vendorEmail`.
   */
  email?: string;
  /** Vendor codes only: the name the vendor gave their company at sign-up, written
   *  back onto the code at redemption — the contractor cannot read the vendor's
   *  user record, and this is how their list shows a name instead of an address. */
  redeemedCompany?: string;
  /** A vendor's admin invite: joins the vendor's own account (not a new one) and
   *  carries the company's key so the new login sees the company's work. */
  member?: boolean;
  vendorKey?: string;
  /** Legacy: the responsible-party name a code was minted for. Unused. */
  party?: string;
  /** Legacy, link model. Unused. */
  linkIds?: string[];
  /** Who sent it, shown on the join screen so the person knows what this is. */
  fromCompany?: string;
  /** 0 means unlimited — a vendor sending a whole crew through one code. */
  maxUses: number;
  uses: number;
  revoked?: boolean;
  note?: string;
  createdBy: string;
  createdAt: string;
  lastUsedBy?: string;
  lastUsedAt?: string;
}

/** A mailto: that opens their mail app with the admin invite written. */
export function adminInviteMailto(email: string, company: string, body: string) {
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(`Admin on ${company || 'our'} BlueTape Pro account`)}&body=${encodeURIComponent(body)}`;
}

export const inviteOpen = (i: Invite) =>
  !i.revoked && (i.maxUses === 0 || (i.uses ?? 0) < i.maxUses);

export const normEmail = (e: string | null | undefined) => (e ?? '').trim().toLowerCase();
export const looksLikeEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normEmail(e));

/**
 * A vendor as the contractor knows them: an email that work is assigned to, and
 * the name the contractor gave them. Derived from the vendor codes this account
 * has minted (the registry) and from whatever the items already carry — there is
 * no vendor document to read across accounts, and none is needed.
 */
export interface VendorRef {
  /** The address the vendor was first reached at. The key until they sign up. */
  email: string;
  company: string;
  /** The code was redeemed — somebody signed in with this address. */
  joined: boolean;
  /** The vendor's account id, once known (the login that redeemed the code, or
   *  what the items already carry). The key from then on. */
  accountId?: string;
  /** From the vendor's own profile, when they have published one. */
  contact?: string;
  phone?: string;
  contactEmail?: string;
  projects: string[];
  /** The open code, if one is still out for them. */
  code?: string;
}

/**
 * What a vendor chooses to show the builders it works for: its name, who to call,
 * the shop number, the shop email. Written by the vendor's owner, readable by any
 * sign-in — the builder's registry reads it by the vendor's account id. Changing
 * any of it changes nothing downstream: work is keyed to the account.
 */
export interface VendorProfile {
  company: string;
  name: string;
  phone: string;
  email: string;
  updatedAt: string;
}

export function vendorRegistry(invites: Invite[], items: Item[],
                               profiles: Record<string, VendorProfile> = {}): VendorRef[] {
  const m = new Map<string, VendorRef>();
  for (const i of invites) {
    if (i.role !== 'vendor' || !i.email || i.member) continue;
    const e = normEmail(i.email);
    const joined = (i.uses ?? 0) > 0 || !!i.lastUsedBy;
    const cur = m.get(e);
    // The name they gave themselves wins; the contractor's own label next; the
    // address until either exists.
    const company = i.redeemedCompany || i.company || i.party || e;
    if (!cur) m.set(e, { email: e, company, joined, projects: [...i.projects],
                         accountId: i.lastUsedBy || undefined,
                         code: inviteOpen(i) ? i.code : undefined });
    else {
      cur.joined = cur.joined || joined;
      cur.accountId = cur.accountId || i.lastUsedBy || undefined;
      cur.projects = [...new Set([...cur.projects, ...i.projects])];
      if (cur.company === e && company !== e) cur.company = company;
      if (!cur.code && inviteOpen(i)) cur.code = i.code;
    }
  }
  for (const it of items) {
    if (!it.vendorEmail) continue;
    const e = normEmail(it.vendorEmail);
    const cur = m.get(e);
    if (!cur) m.set(e, { email: e, company: it.lead || e, joined: !!it.vendorId, accountId: it.vendorId || undefined,
                         projects: [it.projectId] });
    else {
      if (!cur.projects.includes(it.projectId)) cur.projects.push(it.projectId);
      if (it.vendorId) { cur.accountId = cur.accountId || it.vendorId; cur.joined = true; }
    }
  }
  // What the vendor says about itself beats what the code remembers.
  for (const v of m.values()) {
    const pr = v.accountId ? profiles[v.accountId] : undefined;
    if (!pr) continue;
    if (pr.company) v.company = pr.company;
    v.contact = pr.name || undefined;
    v.phone = pr.phone || undefined;
    v.contactEmail = pr.email || undefined;
  }
  return [...m.values()].sort((a, b) => a.company.localeCompare(b.company));
}

/** Legacy: the short-lived link model of 2026-09-13. Read only by the founding
 *  admin, only to move items and vendors onto email. */
export interface Link {
  id: string;
  projectId: string;
  projectName: string;
  contractorAccountId: string;
  contractorCompany: string;
  party: string;
  vendorAccountId: string;
  vendorCompany: string;
  inviteCode: string;
  createdBy: string;
  createdAt: string;
  joinedAt?: string;
  claimedWith?: string;
}

/** No 0/O/1/I — these get read aloud on a job site. Six random characters
 *  leave enough room that a predictable company prefix is not the secret. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function makeInviteCode(prefix = ''): string {
  const pick = (n: number) => secureCode(n, CODE_ALPHABET);
  const head = prefix.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || pick(2);
  return `${head}-${pick(6)}`;
}
export const normalizeCode = (v: string) => v.trim().toUpperCase().replace(/\s+/g, '');

/** The link a contractor sends. `/join/AB-1234` reads better than a query string
 *  and the SPA redirect already serves it. */
export const joinLink = (code: string, origin = typeof location !== 'undefined' ? location.origin : '') =>
  `${origin}/join/${code}`;

/**
 * The words the invite goes out in — one place, so the automatic email and the
 * mail draft the app falls back to say exactly the same thing.
 */
export function signupInviteBody(name: string, code: string, origin?: string) {
  const link = joinLink(code, origin ?? (typeof location !== 'undefined' ? location.origin : 'https://www.bluetape.pro'));
  return [
    `${name ? name.split(' ')[0] + ',' : 'Hi,'}`,
    '',
    'Your BlueTape Pro account is ready to set up. Open this link and it walks you',
    'through the rest — you pick your company name and a password, and that is it:',
    '',
    link,
    '',
    `If the link does not open, go to ${origin ?? 'https://www.bluetape.pro'} and enter this code: ${code}`,
    '',
    'It works once, and only for this email address.',
  ].join('\n');
}

/** The same invite as a mail draft, for when the send endpoint is not there. */
export const signupMailto = (email: string, name: string, code: string) =>
  `mailto:${encodeURIComponent(email)}`
  + `?subject=${encodeURIComponent('Your BlueTape Pro invite')}`
  + `&body=${encodeURIComponent(signupInviteBody(name, code))}`;

/** Pull a code out of the URL — either /join/AB-1234 or ?invite=AB-1234. */
export function readJoinCode(): string {
  if (typeof location === 'undefined') return '';
  const fromPath = location.pathname.match(/^\/join\/([^/?#]+)/i)?.[1];
  const fromQuery = new URLSearchParams(location.search).get('invite');
  return normalizeCode(decodeURIComponent(fromPath || fromQuery || ''));
}

/** Drop the invite out of the address bar once it's been used or declined, so a
 *  reload doesn't try to redeem it again. */
export function clearJoinCode() {
  if (typeof history === 'undefined') return;
  try { history.replaceState({}, '', location.origin + '/'); } catch { /* no-op */ }
}

/** The message a contractor pastes into a text or email. */
export function inviteMessage(opts: {
  code: string; from: string; projectNames: string[]; company?: string; origin?: string;
  /** A second admin on the sender's own account. */
  admin?: boolean;
  /** A code that founds their own account reads nothing like joining a job. */
  newAccount?: boolean; fromCompany?: string;
  /** A vendor code: their company's seat on the job, new account or not. */
  vendor?: boolean;
  /** The address the work is assigned to — the one they must sign up with. */
  email?: string;
}) {
  if (opts.newAccount) {
    const sender = opts.fromCompany ? `${opts.from} at ${opts.fromCompany}` : opts.from;
    return `${sender} set you up on BlueTape Pro — punch lists for your jobs, your crew and your subs.\n\n`
      + `Tap to create your account:\n${joinLink(opts.code, opts.origin)}\n\n`
      + `Your code is ${opts.code} if you'd rather type it in at ${(opts.origin || '').replace(/^https?:\/\//, '') || 'the site'}.`;
  }
  if (opts.admin) {
    const co = opts.company || opts.fromCompany || 'our';
    return `${opts.from} made you an admin on ${co}'s BlueTape Pro account — every job, every crew, the same run of it they have.\n\n`
      + `Tap to set up your login:\n${joinLink(opts.code, opts.origin)}\n\n`
      + `Your code is ${opts.code} if you'd rather type it in at ${(opts.origin || '').replace(/^https?:\/\//, '') || 'the site'}.`;
  }
  if (opts.vendor) {
    const sender = opts.fromCompany ? `${opts.from} at ${opts.fromCompany}` : opts.from;
    const where = opts.projectNames.length ? opts.projectNames.join(' and ') : 'a job';
    const co = opts.company ? ` for ${opts.company}` : '';
    return `${sender} is setting you up${co} on BlueTape Pro — the punch list${opts.projectNames.length ? ` for ${where}` : ''}.\n\n`
      + `Tap to create your account:\n${joinLink(opts.code, opts.origin)}\n\n`
      + `Your work is assigned to ${opts.email || 'this email address'}, so sign up with that address. `
      + `Already have a BlueTape account under it? Just sign in — it's already there.`;
  }
  const where = opts.projectNames.length ? opts.projectNames.join(' and ') : 'a job';
  const who = opts.company ? ` for ${opts.company}` : '';
  return `${opts.from} added you${who} to ${where} on BlueTape Pro — the punch list for the job.\n\n`
    + `Tap to set up your login:\n${joinLink(opts.code, opts.origin)}\n\n`
    + `Your code is ${opts.code} if you'd rather type it in at ${(opts.origin || '').replace(/^https?:\/\//, '') || 'the site'}.`;
}

export interface Photo {
  id: string;
  url: string;
  by: string;
  at: string;
  /** true when taken as part of closing the item out */
  onClose?: boolean;
}

/**
 * One entry in an item's note log. Notes are never edited or removed — Stephen,
 * 2026-09-14: "I don't want a note to overwrite the previous note. It should be
 * added to the item card with a date and time stamp. This is for accountability
 * and transparency." The rules hold the list append-only for every seat.
 */
export interface ItemNote {
  id: string;
  text: string;
  by: string;
  /** ISO time it was written. */
  at: string;
}

/** The item's notes, oldest first — including a note written before the log
 *  existed, which lived in `fieldNote`/`noteBy` alone. */
export function notesOf(it: Item): ItemNote[] {
  const list = it.notes ?? [];
  if (list.length) return list;
  return it.fieldNote
    ? [{ id: 'legacy', text: it.fieldNote, by: it.noteBy || '', at: it.updatedAt || '' }]
    : [];
}

/** The patch that appends a note: the log grows by one, and `fieldNote`/`noteBy`
 *  keep carrying the latest so the row icon, search and exports still read it. */
export function appendNote(it: Item, text: string, by: string, at = new Date().toISOString()): Partial<Item> {
  const note: ItemNote = { id: newId(), text: text.trim(), by, at };
  return { notes: [...notesOf(it), note], fieldNote: note.text, noteBy: by };
}

/** "Sep 14, 12:40 AM" — for a note stamp. */
export function whenLabel(at: string): string {
  const d = new Date(at);
  if (!at || !Number.isFinite(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export interface Item {
  /** Opaque document id, globally unique. Never shown. */
  id: string;
  /** The human code — "HM-001". Unique within its project, not across accounts,
   *  which is exactly why it stopped being the document id. Absent on records
   *  written before the change; fall back to `id` with refOf(). */
  ref?: string;
  projectId: string;
  /** Denormalised from the project so a contractor's query can filter on it. */
  accountId?: string;
  seq: number;
  area: string;
  item: string;
  /** free text the person fixing it needs; from the walkthrough or typed in */
  details: string;
  /** full responsible string as written, e.g. "Cedar Ridge/Marcus Hale" */
  trade: string;
  /** The responsible party as a name — the first party in `trade`, or whatever the
   *  contractor reassigned it to. Display, filters and grouping go by this. */
  lead: string;
  /** The vendor ACCOUNT this item belongs to — the vendor's account id. The key,
   *  since 2026-09-14: the vendor's query filters on it and the rules compare it
   *  to the signed-in account. Set when the builder hands work to a vendor that
   *  has signed up, or stamped afterwards by the builder's app once the address
   *  below has been claimed. '' or absent until then. */
  vendorId?: string;
  /** The address the work was handed to, lowercased — how a vendor that does not
   *  exist yet is reached, and the key for items until `vendorId` is stamped. '' means
   *  nobody receives it yet. Contact detail thereafter, never a key again. */
  vendorEmail?: string;
  /** Legacy, link model. Unused. */
  linkId?: string;
  /** Display name of the field tech, kept in step with techName. */
  assignee: string;
  /** The field tech executing the work: the id of a `techs/{id}` record (before
   *  2026-09-13 late, a user uid — old records still read). Scoping matches on
   *  this id, not a name — two techs called Dave would otherwise see each
   *  other's items. */
  techUid?: string;
  techName?: string;
  status: Status;
  /** The latest note's text, kept for the row icon, search and exports. The
   *  record is `notes`; before it existed this was the only note. */
  fieldNote: string;
  noteBy: string;
  /** Every note ever left on the item, oldest first, never edited. */
  notes?: ItemNote[];
  /** Added by the client through their link. Sits on the contractor's list as a
   *  request until `accepted`; declining it is deleting it. */
  fromClient?: boolean;
  accepted?: boolean;
  /** Who added it and when — for client-added items, the client's name. */
  addedBy?: string;
  addedAt?: string;
  doneBy: string;
  doneAt: string;
  /** Target finish date, `YYYY-MM-DD`. '' or absent means none set. */
  dueDate?: string;
  /** Estimated hours of work. 0 or absent means none set. */
  estHours?: number;
  /** ISO time the current Working run began. '' or absent when not running. */
  startedAt?: string;
  /** Milliseconds banked from previous Working runs. Punch work is intermittent —
   *  an item gets started, parked for a part, and picked up again — so time
   *  accumulates across runs rather than measuring first-start to done. */
  workedMs?: number;
  /** Elapsed total an admin signed off on. Absent means never reviewed. */
  reviewedMs?: number;
  reviewedBy?: string;
  reviewedAt?: string;
  photos: Photo[];
  /** Stamped with the job when the owning account is deleted — a vendor reads
   *  its work off items, so the stamp has to reach them too. */
  deletedAt?: string;
  updatedBy: string;
  updatedAt: string;
}

export type EventKind =
  | 'added' | 'done' | 'reopen' | 'start'
  | 'assign' | 'note' | 'photo' | 'import'
  | 'schedule' | 'removed' | 'moved' | 'review';

export interface PunchEvent {
  id: string;
  /** Denormalised so the log can be scoped without reading the project. */
  accountId?: string;
  projectId: string;
  /** The item's opaque document id. Never shown. */
  itemId: string;
  /** The human code ("HM-042") at the time of the event, since the id is opaque
   *  and the item may be gone by the time anyone reads the log. Absent on events
   *  written before this field; the UI falls back to the item's current ref. */
  itemRef?: string;
  kind: EventKind;
  by: string;
  at: string;
  area: string;
  item: string;
  detail: string;
}

export const STATUS_LABEL: Record<Status, string> = {
  open: 'Open', progress: 'Working', done: 'Done',
};

export const EVENT_LABEL: Record<EventKind, string> = {
  added: 'Added', done: 'Closed out', reopen: 'Reopened', start: 'Started',
  assign: 'Reassigned', note: 'Field note', photo: 'Photo added', import: 'Imported',
  schedule: 'Scheduled', removed: 'Deleted', moved: 'Moved', review: 'Time reviewed',
};

/** First party in a slash-separated responsible string. */
export function leadOf(trade: string): string {
  if (!trade) return 'TBD';
  const parts = trade.split('/').map(p => p.trim().replace(/\?+$/, '').trim()).filter(Boolean);
  const first = parts[0] || 'TBD';
  return /^tbd$/i.test(first) ? 'TBD' : first;
}

/** Local calendar date as `YYYY-MM-DD` — never use toISOString(), it's UTC and
 *  rolls the date over in the evening on the west coast. */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Past due and still open. A closed item is never overdue, however late it was. */
export function isOverdue(it: Item): boolean {
  return !!it.dueDate && it.status !== 'done' && it.dueDate < today();
}

/** Whole days from today; negative is in the past. */
export function daysUntil(date: string): number {
  if (!date) return 0;
  const [y, m, d] = date.split('-').map(Number);
  const then = new Date(y, m - 1, d).getTime();
  const [ty, tm, td] = today().split('-').map(Number);
  const now = new Date(ty, tm - 1, td).getTime();
  return Math.round((then - now) / 86400000);
}

/** "Due Sep 20" / "Overdue 3 days" / "Due today" — for a chip, not a sentence. */
export function dueLabel(it: Item): string {
  if (!it.dueDate) return '';
  const n = daysUntil(it.dueDate);
  const [y, m, d] = it.dueDate.split('-').map(Number);
  const when = new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (it.status === 'done') return `Due ${when}`;
  if (n < 0) return `Overdue ${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'}`;
  if (n === 0) return 'Due today';
  return `Due ${when}`;
}

/** Total estimated hours across items that carry an estimate. */
export function estTotal(items: Item[]): number {
  return items.reduce((sum, i) => sum + (i.estHours || 0), 0);
}

/** "45 min" / "2 hrs" / "41 hrs". Anything under an hour reads as minutes —
 *  rounding to one decimal turned a short estimate into a bare "0 hrs". */
export function hoursLabel(h: number): string {
  if (!h || h < 0) return '';
  if (h < 1) {
    const m = Math.max(1, Math.round(h * 60));
    return `${m} min`;
  }
  const n = Math.round(h * 10) / 10;
  return `${n} ${n === 1 ? 'hr' : 'hrs'}`;
}

/** Tolerates the older plain-string shape as well as {name, note}. */
export function asEntries(v: unknown): RosterEntry[] {
  if (!Array.isArray(v)) return [];
  return v
    .map(e => (typeof e === 'string' ? { name: e } : e as RosterEntry))
    .filter(e => e && typeof e.name === 'string' && e.name.trim())
    .map(e => ({ ...e, name: e.name.trim() }));
}

export function rosterOf(p: Project | null | undefined, kind: 'area' | 'trade'): RosterEntry[] {
  return asEntries(kind === 'area' ? p?.areas : p?.trades)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Distinct values the items actually carry — used ONLY to seed a roster that
 *  doesn't exist yet, never to define one that does. */
export function usedValues(items: Item[], kind: 'area' | 'trade'): string[] {
  return [...new Set(items.map(i => (kind === 'area' ? i.area : i.trade)).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
}

/** Banked time plus the run in progress. `now` is passed in so a list of rows
 *  ticks off one clock instead of each row reading Date.now() separately. */
export function elapsedMs(it: Item, now = Date.now()): number {
  const banked = it.workedMs ?? 0;
  if (it.status !== 'progress' || !it.startedAt) return banked;
  const started = new Date(it.startedAt).getTime();
  if (!Number.isFinite(started)) return banked;
  return banked + Math.max(0, now - started);
}

export const isRunning = (it: Item) => it.status === 'progress' && !!it.startedAt;

/**
 * What a status change does to the clock. Starting puts the item on it; anything
 * that leaves Working banks the run. Time is derived from timestamps, not counted
 * in the browser, so closing the app or swapping phones doesn't lose it. Shared
 * by the main app and the tech page so the two can never disagree.
 */
export function clockPatch(it: Item, next: Status, now = Date.now()): Partial<Item> {
  if (next === 'progress') {
    return isRunning(it) ? {} : { startedAt: new Date(now).toISOString() };
  }
  if (isRunning(it)) {
    const ran = Math.max(0, now - new Date(it.startedAt!).getTime());
    return { workedMs: (it.workedMs ?? 0) + ran, startedAt: '' };
  }
  return it.startedAt ? { startedAt: '' } : {};
}

/** "45s" · "12m" · "1h 24m" · "3d 4h" — compact enough for a list row. */
export function duration(ms: number): string {
  if (!ms || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), rm = m % 60;
  if (h < 24) return rm ? `${h}h ${rm}m` : `${h}h`;
  const d = Math.floor(h / 24), rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

/** Work may run half again past the estimate before anyone needs to look at it. */
export const OVERRUN_FACTOR = 1.5;
/** No estimate means no ratio to breach, so fall back to catching a clock that
 *  was plainly left running — the original point of the guard. */
export const STALE_RUN_MS = 12 * 60 * 60 * 1000;

export const estMs = (it: Item) => (it.estHours || 0) * 3600_000;

/** The point past which the time needs an admin's eyes. */
export function overrunLimit(it: Item): number {
  const est = estMs(it);
  return est > 0 ? est * OVERRUN_FACTOR : STALE_RUN_MS;
}

/**
 * Flagged when the clock is past the limit and nobody has signed it off. After a
 * review the bar moves out by another half-estimate, so a long job asks again as
 * it keeps drifting instead of going quiet for good after one acknowledgement.
 */
export function needsReview(it: Item, now = Date.now()): boolean {
  if (it.status === 'done' && !it.reviewedMs) {
    // A closed-out item is still worth flagging if it blew the estimate.
    return elapsedMs(it, now) > overrunLimit(it) && estMs(it) > 0;
  }
  const spent = elapsedMs(it, now);
  if (spent <= overrunLimit(it)) return false;
  if (it.reviewedMs == null) return true;
  const grace = estMs(it) > 0 ? estMs(it) * 0.5 : STALE_RUN_MS;
  return spent > it.reviewedMs + grace;
}

/** How far past the estimate, as a multiple. 0 when there's no estimate. */
export function overrunRatio(it: Item, now = Date.now()): number {
  const est = estMs(it);
  return est > 0 ? elapsedMs(it, now) / est : 0;
}

/** Kept for the no-estimate case: a clock plainly left running. */
export const isStaleRun = (it: Item, now = Date.now()) =>
  isRunning(it) && now - new Date(it.startedAt!).getTime() > STALE_RUN_MS;

/** What a human should see for an item. Older records have no `ref`, and their
 *  `id` already is the human code, so this reads correctly either way. */
export const refOf = (it: { ref?: string; id: string }) => it.ref || it.id;

/** A filename-safe version of a name. */
export const slug = (v: string) =>
  String(v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** What to call a project in a filename: its short name, never its id — the id
 *  is opaque now and means nothing on somebody's desktop. */
export const projectSlug = (p: { short?: string; name?: string } | null | undefined) =>
  (p && slug(p.short || p.name || '')) || 'punch-list';

/**
 * An opaque id. Names made terrible keys: two contractors with a "Harlow" job,
 * or the same `HM` prefix, collided on the same document path — the write was
 * refused mid-import, half the items already written.
 */
export function newId(): string {
  const a = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const rnd = Array.from({ length: 14 }, () => a[Math.floor(Math.random() * a.length)]).join('');
  return Date.now().toString(36) + rnd;
}

export function counts(items: Item[]) {
  const c = { open: 0, progress: 0, done: 0, total: items.length };
  for (const i of items) c[i.status ?? 'open']++;
  return c;
}

export function pct(c: { done: number; total: number }) {
  return c.total ? Math.round((c.done / c.total) * 100) : 0;
}
